import { planItemCompletesStage } from './reportPlan';
import { reportTypeStage } from '../../config/reportTypes';
import {
  POWER_STRATEGY_HEADLINE,
  PROMOTE_SCENARIO_HEADLINE,
  STAGE2_DECISIONS_CTA,
  stage2DecisionsHeadline,
} from './stageActionCopy';
import { stage2FocusField } from '../stage2/stage2Catalogue';
/**
 * PATH TO GO — single highest-leverage NEXT ACTION resolver.
 *
 * Pure module: derives the one action the cockpit should surface ("what is the
 * one thing to do now?") from the derived `PathToGoState` + the oracle
 * composite. Extracted from `PathToGoNextAction.tsx` so every surface that
 * renders the next action (the wizard card, the guided-mode bar, the Home
 * journey band) resolves it through ONE deterministic path.
 *
 * Resolution order (highest leverage first):
 *   1. Active screening, then runnable location screening and refinement.
 *   2. Missing or conflicting decision-defining intent.
 *   3. Outstanding required Stage 1 work, using its canonical action.
 *   4. Stage 2 decisions, once screening is complete and before a scenario is
 *      promoted — guidance only: a deferred decision drops out.
 *   5. The first ready mature analysis, then its blocked prerequisites.
 *   6. Remaining stale, held, or failed required analyses.
 *   7. The remaining adequacy / exact-lift / optional-strengthening work.
 *   8. Nothing open → a cleared confirmation (GO reached or all work cleared).
 *
 * The EXACT band impact (when one exists) is the real `projectPillarShift`
 * re-run — never a linear estimate (ENGINEERING RULE 6: faithful to the oracle).
 * Deterministic; same inputs → same action.
 */
import {
  perPillarMarginalDrag,
  projectPillarShift,
} from './distanceToGo';
import { PILLARS } from '../portfolio/pillarRegistry';
import {
  DECISION_BAND_LABEL,
  DEFAULT_BANDS,
  type DecisionBandConfig,
  type DecisionBandId,
} from '../portfolio/decisionBands';
import type {
  PathToGoState,
  PathToGoPhaseId,
  PathToGoStageId,
} from './pathToGoOrchestrator';
import { getPathToGoAnalysisPlan } from './pathToGoOrchestrator';
import type { NextStep, NextStepKind } from './nextStep';
import type { PathAction, ReportPlanItem } from './reportPlan';
import type { SiteComposite } from '../../types/portfolioComposite';

/** Same STRONG-pillar target the DecisionStage uses, kept in lock-step. */
const STRONG_PILLAR_TARGET = 4.5;

/**
 * Re-exported from the ONE canonical source rather than re-declared. This map
 * was one of seven copies of the same four strings — the reason a rename could
 * land as a partial cutover with surfaces disagreeing about the same site.
 */
export const BAND_LABEL: Record<DecisionBandId, string> = DECISION_BAND_LABEL;

/** DEFAULT_BANDS best→worst (lowest min first), for "is this band better?" */
const BANDS_BEST_FIRST = [...DEFAULT_BANDS].sort((a, b) => a.min - b.min);

function bandRank(id: DecisionBandId): number {
  return BANDS_BEST_FIRST.findIndex((b) => b.id === id);
}

/** Which stage owns a step of this kind (the CTA destination). */
export const KIND_STAGE: Record<NextStepKind, PathToGoStageId> = {
  intent: 'intent',
  evidence: 'evidence',
  resolve: 'resolve',
  generate: 'generate',
};

/** Shape of the resolved single next action. */
export interface NextAction {
  /** Stable test/analytics key. */
  key: string;
  /** Imperative, action-first headline (e.g. "Strengthen Power"). */
  headline: string;
  /** One specific line of why / what (data-specific). */
  detail: string;
  /** The EXACT band this action lifts the site to, when known. */
  liftsBandTo?: DecisionBandId;
  /** CTA label + destination stage. */
  ctaLabel: string;
  targetStage: PathToGoStageId;
  targetPhase: PathToGoPhaseId;
  pathAction: PathAction;
  /** True for a "you're done" confirmation (renders the cleared variant). */
  cleared?: boolean;
  /** Stage-based recommendation or status category label replacing generic "Do this next". */
  categoryLabel?: string;
}

function phaseForStage(stage: PathToGoStageId): PathToGoPhaseId {
  if (stage === 'intent') return 'define';
  if (stage === 'generate') return 'build';
  if (stage === 'decision') return 'decide';
  return 'verify';
}

function stageForPathAction(action: PathAction): PathToGoStageId {
  switch (action.kind) {
    case 'intent':
    case 'site-boundary':
      return 'intent';
    case 'report-setup':
    case 'report-progress':
    case 'analysis-open':
    case 'generation-impact':
    case 'sed-rebuild':
    case 'bom':
    case 'financial-inputs':
    case 'engineering-source':
      return 'generate';
    case 'report-qa':
    case 'audit':
    case 'evidence':
      return 'resolve';
    case 'request-access':
      return action.destination === 'intent' ? 'intent' : 'generate';
    case 'none':
      return 'decision';
  }
}

function pathActionForStep(step: NextStep): PathAction {
  if (step.id === 'site-basis:boundary') {
    return { kind: 'site-boundary', label: step.resolver?.label ?? step.label };
  }
  if (step.kind === 'intent' || step.resolver?.action === 'open-intent') {
    const targetId = step.resolver?.targetId;
    return {
      kind: 'intent',
      label: step.resolver?.label ?? 'Confirm intent',
      ...(targetId ? { field: targetId } : {}),
      // Stage 2 decision anchors live in the Design & report assumptions view.
      ...(targetId?.startsWith('stage2:') ? { view: 'design' as const } : {}),
    };
  }
  if (step.resolver?.action === 'open-tab') {
    if (step.resolver.targetId === 'generation') {
      return {
        kind: 'generation-impact',
        label: step.resolver.label,
        focus: 'scenario',
      };
    }
    if (step.resolver.targetId === 'financial') {
      return { kind: 'financial-inputs', label: step.resolver.label };
    }
    if (step.resolver.targetId === 'supplychain') {
      return { kind: 'bom', label: step.resolver.label };
    }
  }
  if (step.kind === 'resolve') {
    return { kind: 'audit', label: step.resolver?.label ?? 'Resolve blocker' };
  }
  return {
    kind: 'evidence',
    label: step.resolver?.label ?? 'Close evidence gaps',
    ...(step.resolver?.targetId ? { targetId: step.resolver.targetId } : {}),
  };
}

/**
 * The detail line for a saved report that no current decision analysis backs
 * (`savedWithoutCurrentAnalysis`, gate mode). It has no `blockingReason` on
 * purpose, and the default authority detail ("No additional publishing
 * authority is required.") answered a question nobody asked.
 */
export const SAVED_WITHOUT_CURRENT_ANALYSIS_DETAIL =
  'A saved report exists, but no current decision analysis is loaded for it, so it does not count toward GO.';

/**
 * Where the next step ranks a row: a never-run row ('ready') and a saved row
 * relabelled "Refresh required" only because no current analysis is loaded
 * (2026-09-18) rank TOGETHER, in plan order — exactly where that saved row
 * ranked while it read 'ready'. The relabel is presentation; keeping its
 * rank keeps the cockpit on the same row Path to GO › Analyse and the
 * Analyses page start with (`buildStageBoards().next`, plan order).
 * A genuinely stale / held / failed row still ranks behind never-run work.
 */
function ranksAsReady(item: ReportPlanItem): boolean {
  return item.lifecycle === 'ready' || item.savedWithoutCurrentAnalysis === true;
}

/**
 * A Stage 2 analysis waiting on the promoted scenario (`stageLockFor`). Its
 * lifecycle can read 'ready' (nothing of its OWN is missing — Environmental
 * since the 2026-09-30 stage move, or Financial with a complete model) while
 * the enqueue chokepoint refuses it, so it is never offered as "run" or
 * "retry" work: the Stage 2 hold routes the reader to the scenario instead,
 * as Path to GO › Analyse and the Analyses boards already do.
 */
function isStageLocked(item: ReportPlanItem): boolean {
  return item.eligibility?.reasonCode === 'stage-locked';
}

function actionFromReportItem(item: ReportPlanItem): NextAction {
  const stage = stageForPathAction(item.primaryAction);
  const isReady = item.lifecycle === 'ready';
  const active = item.lifecycle === 'queued' || item.lifecycle === 'generating';
  const isGenerationScenarioPrereq =
    item.primaryAction.kind === 'generation-impact' ||
    Boolean(item.blockingReason?.toLowerCase().includes('generation scenario')) ||
    Boolean(item.authority?.detail?.toLowerCase().includes('generation scenario'));

  const isStage2 = reportTypeStage(item.type) === 2;
  const categoryLabel = isGenerationScenarioPrereq || isStage2 ? 'Screening recommendations' : undefined;

  const headline = item.lifecycle === 'queued' || item.lifecycle === 'generating'
    ? 'Follow the analysis progress'
    : isReady
    ? `Run ${item.type === 'test-fit' ? 'the Test Fit analysis' : 'the next required analysis'}`
    : item.type === 'test-fit' && item.lifecycle === 'dependency-blocked'
      ? 'Complete the Test Fit authority chain'
      : item.lifecycle === 'refresh-needed'
        // "stale" only when the plan says so: a saved report awaiting its
        // decision analysis is refresh work that nothing marked stale.
        ? `Refresh ${item.type === 'test-fit' ? 'the Test Fit analysis' : item.stale ? 'the stale analysis' : 'the analysis'}`
        : item.lifecycle === 'preliminary'
          ? 'Complete the written analysis'
          : item.lifecycle === 'preview'
            ? item.primaryAction.kind === 'report-setup'
              ? reportTypeStage(item.type) === 1 ? 'Generate the written screening report' : 'Generate the written report'
              : 'Review the analysis preview'
          : item.lifecycle === 'quality-held'
            ? 'Resolve the analysis quality hold'
            : item.lifecycle === 'failed-coverage'
              ? 'Resolve the provider coverage block'
              : item.lifecycle === 'failed'
                ? 'Retry the failed analysis'
              : item.lifecycle === 'unavailable'
                ? 'Enable Generation & Impact'
              : isGenerationScenarioPrereq
                ? 'Apply screening recommendations to Generation & Impact'
                : 'Complete the next analysis prerequisite';

  const detail = isGenerationScenarioPrereq
    ? 'Review the screening recommendations against the declared power strategy, then configure and promote the required generation scenario to continue Stage 2 validation.'
    : active
      ? 'This run is in progress. Its result remains pending until the analysis finishes and passes the current evidence and quality checks.'
      : item.blockingReason
        ?? (item.savedWithoutCurrentAnalysis ? SAVED_WITHOUT_CURRENT_ANALYSIS_DETAIL : undefined)
        ?? item.authority.detail
        ?? 'The analysis is ready to run.';

  return {
    key: `report:${item.type}:${item.lifecycle}`,
    headline,
    detail,
    ctaLabel: item.primaryAction.label,
    targetStage: stage,
    targetPhase: phaseForStage(stage),
    pathAction: item.primaryAction,
    ...(categoryLabel ? { categoryLabel } : {}),
  };
}

/**
 * The exact band-changing pillar lift (worst-drag-first). Mirrors the
 * DecisionStage's `computeLiftRows` selection but returns only the single best
 * (highest-drag) pillar whose `projectPillarShift → STRONG` strictly improves
 * the band. Returns null when no single-pillar fix moves the band.
 */
export function bestExactLift(
  composite: SiteComposite,
  bandConfig: DecisionBandConfig | null | undefined,
): { pillarLabel: string; toBand: DecisionBandId } | null {
  const currentRank = bandRank(composite.decisionBand);
  for (const d of perPillarMarginalDrag(composite)) {
    if (d.score >= STRONG_PILLAR_TARGET) continue;
    const proj = projectPillarShift(composite, d.pillarId, STRONG_PILLAR_TARGET, bandConfig);
    const projRank = bandRank(proj.band.id);
    if (proj.bandChanged && projRank >= 0 && projRank < currentRank) {
      return { pillarLabel: PILLARS[d.pillarId].label, toBand: proj.band.id };
    }
  }
  return null;
}

/**
 * Resolve the single highest-leverage next action from the derived state +
 * oracle composite. Deterministic; same inputs → same action.
 */
export function resolveNextAction(
  state: PathToGoState,
  composite: SiteComposite | null,
  bandConfig: DecisionBandConfig | null | undefined,
): NextAction {
  const { verdict, adequacyGate, rankedSteps, distanceToGo } = state;
  const withheld = !adequacyGate.adequate || verdict.band == null;
  const analysisPlan = getPathToGoAnalysisPlan(state);

  // Follow an accepted screening run before suggesting another launch or
  // retry. Other work remains available in the plan; active work alone never
  // establishes current evidence or changes any launch prerequisite.
  const activeScreening = analysisPlan.find(item => item.role === 'required'
    && reportTypeStage(item.type) === 1
    && !planItemCompletesStage(item)
    && (item.lifecycle === 'queued' || item.lifecycle === 'generating'));
  if (activeScreening) return actionFromReportItem(activeScreening);

  // Existing report history does not make optional intent mandatory for the
  // next location screen. Use only a canonically runnable screening action;
  // source, access, quality and mature-analysis prerequisites stay intact.
  const locationScreening = state.hasScreeningCoordinates || state.coordinateFirstBootstrap
    ? analysisPlan.find(item => item.role === 'required'
        && reportTypeStage(item.type) === 1
        && !planItemCompletesStage(item)
        && ['ready', 'refresh-needed'].includes(item.lifecycle)
        && item.primaryAction.kind === 'report-setup')
    : undefined;
  if (locationScreening) return actionFromReportItem(locationScreening);

  // Intent remains the next prerequisite when no location screen can run.
  const intentStep = rankedSteps.find((step) => step.kind === 'intent');
  if (intentStep) {
    const pathAction = pathActionForStep(intentStep);
    return {
      key: `step:${intentStep.id}`,
      headline: intentStep.label,
      detail: intentStep.why,
      ctaLabel: pathAction.label,
      targetStage: 'intent',
      targetPhase: 'define',
      pathAction,
    };
  }

  // Stage 1 remains the next milestone even when an older/missing screen is
  // stale or held. Global lifecycle ordering used to choose a Stage 2 scenario
  // prerequisite before those screens. Reuse each item's existing action so
  // source rebuilds, quality holds and running jobs retain their own guards.
  const openScreening = analysisPlan.filter(item => item.role === 'required'
    && reportTypeStage(item.type) === 1 && !planItemCompletesStage(item));
  const screeningNext = openScreening.find(ranksAsReady)
    ?? openScreening.find(item => ['preliminary', 'preview', 'refresh-needed', 'quality-held', 'failed'].includes(item.lifecycle))
    ?? openScreening.find(item => ['needs-input', 'dependency-blocked', 'unavailable'].includes(item.lifecycle))
    ?? openScreening.find(item => item.lifecycle === 'queued' || item.lifecycle === 'generating');
  if (screeningNext) return actionFromReportItem(screeningNext);

  // Stage 2 decisions — early screening is complete and mature validation
  // needs design choices before a scenario can be promoted. The checklist
  // shows the screening evidence for every option; it guides, never blocks:
  // a deferred decision drops out of the headline and a promoted scenario
  // ends it.
  const stage2 = state.stage2Decisions;
  const stage2Lead = stage2?.available && stage2.headline
    ? stage2.decisions.find((decision) => decision.id === stage2.headline)
    : undefined;
  if (stage2 && stage2Lead) {
    return {
      key: `stage2:${stage2Lead.id}`,
      headline: stage2DecisionsHeadline(stage2.openCount, stage2Lead.label.toLowerCase()),
      detail: `Early screening is complete. Mature validation starts from these decisions, each with the screening evidence for and against its options. Next: ${stage2Lead.label.toLowerCase()}.`,
      ctaLabel: STAGE2_DECISIONS_CTA,
      targetStage: 'intent',
      targetPhase: 'define',
      pathAction: { kind: 'intent', label: STAGE2_DECISIONS_CTA, view: 'design', field: stage2Lead.focusField },
      categoryLabel: 'Screening recommendations',
    };
  }

  // 2. Launch the first ready required analysis (a saved row awaiting its
  // decision analysis ranks here too — see `ranksAsReady`).
  const readyRequired = analysisPlan.find(
    (item) => item.role === 'required'
      && !planItemCompletesStage(item)
      && !isStageLocked(item)
      && ranksAsReady(item),
  );
  if (readyRequired) return actionFromReportItem(readyRequired);

  // 3. Resolve the first prerequisite of a blocked required analysis.
  const blockedRequired = analysisPlan.find(
    (item) =>
      item.role === 'required'
      && !planItemCompletesStage(item)
      && ['needs-input', 'dependency-blocked', 'unavailable'].includes(item.lifecycle),
  );
  if (blockedRequired) return actionFromReportItem(blockedRequired);

  // 4. A stale, held, or failed required analysis is a Verify action.
  const qaRequired = analysisPlan.find(
    (item) =>
      item.role === 'required'
      && !planItemCompletesStage(item)
      && !isStageLocked(item)
      && ['preliminary', 'preview', 'refresh-needed', 'quality-held', 'failed'].includes(item.lifecycle),
  );
  if (qaRequired) return actionFromReportItem(qaRequired);

  // A final development-readiness hold remains authoritative. Its settings
  // belong to the advanced report workflow, not the early screening brief.
  if (withheld && adequacyGate.adequate && composite?.decisionHold) {
    const hold = composite.decisionHold;
    // TWO-STAGE HOLDS (Phase 2) — route to the work that lifts them. A stage
    // hold names outstanding analyses: prefer the plan's own item for the first
    // outstanding required analysis of that stage (same action Path to GO >
    // Analyse shows); a locked Stage 2 routes to the Generation & Impact
    // workspace where a scenario is promoted.
    if (hold.code === 'stage-1-incomplete' || hold.code === 'stage-2-incomplete') {
      const stage = hold.code === 'stage-1-incomplete' ? 1 : 2;
      const outstanding = state.reportPlan.find(
        (item) => item.role === 'required' && !planItemCompletesStage(item) && reportTypeStage(item.type) === stage,
      );
      if (outstanding) {
        const fromItem = actionFromReportItem(outstanding);
        return { ...fromItem, key: `hold:${hold.code}:${outstanding.type}`, detail: hold.reason };
      }
      const fallbackType = state.requiredReports.find((type) => reportTypeStage(type) === stage);
      return {
        key: `hold:${hold.code}`,
        headline: stage === 1 ? 'Complete early screening' : 'Complete mature validation',
        detail: hold.reason,
        ctaLabel: 'Open Analyses',
        targetStage: 'generate',
        targetPhase: 'build',
        pathAction: fallbackType
          ? { kind: 'report-setup', label: 'Open Analyses', reportType: fallbackType }
          : { kind: 'evidence', label: 'Open Analyses' },
        ...(stage === 2 ? { categoryLabel: 'Screening recommendations' } : {}),
      };
    }
    if (hold.code === 'stage-2-locked') {
      return {
        key: `hold:${hold.code}`,
        headline: 'Apply screening recommendations to Generation & Impact',
        detail: 'Review the screening recommendations against the declared power strategy, then configure and promote the required generation scenario to continue Stage 2 validation.',
        ctaLabel: 'Open Generation & Impact',
        targetStage: 'generate',
        targetPhase: 'build',
        pathAction: { kind: 'generation-impact', label: 'Open Generation & Impact', focus: 'promotion' },
        categoryLabel: 'Screening recommendations',
      };
    }
    // `power-strategy-undeclared` — the grid / hybrid / BTM-only strategy is a
    // Stage 2 decision. Route to it in the decision checklist (which writes the
    // same report-setup field), not to a gated analysis.
    return {
      key: `hold:${hold.code}`,
      headline: POWER_STRATEGY_HEADLINE,
      detail: hold.reason,
      ctaLabel: STAGE2_DECISIONS_CTA,
      targetStage: 'intent',
      targetPhase: 'define',
      pathAction: { kind: 'intent', label: STAGE2_DECISIONS_CTA, view: 'design', field: stage2FocusField('power-supply') },
      categoryLabel: 'Screening recommendations',
    };
  }

  // Withheld after the specific dependency checks: close the evidence gap and
  // never promise a decision band.
  if (withheld) {
    const openSteps = rankedSteps.filter((step) => step.kind !== 'intent').length;
    const pathAction: PathAction = { kind: 'evidence', label: 'Close evidence gaps' };
    return {
      key: 'adequacy',
      headline: 'Close the data-adequacy gap',
      detail:
        adequacyGate.reason ??
        `The verdict is withheld until the data is adequate.${openSteps > 0 ? ` ${openSteps} evidence item${openSteps === 1 ? '' : 's'} open.` : ''}`,
      ctaLabel: 'Close evidence gaps',
      targetStage: 'evidence',
      targetPhase: 'verify',
      pathAction,
    };
  }

  const isGo = verdict.band === 'go';

  // 5. An EXACT band-changing pillar lift after required gate work is clear.
  if (!isGo && composite) {
    const lift = bestExactLift(composite, bandConfig);
    if (lift) {
      return {
        key: 'pillar-lift',
        headline: `Strengthen ${lift.pillarLabel}`,
        detail: `Review the ${lift.pillarLabel} evidence. If this topic is supported by strong evidence, the model indicates ${BAND_LABEL[lift.toBand]}.`,
        liftsBandTo: lift.toBand,
        ctaLabel: 'Close evidence gaps',
        targetStage: 'evidence',
        targetPhase: 'verify',
        pathAction: { kind: 'evidence', label: 'Close evidence gaps' },
      };
    }
  }

  // 5. The top remaining ranked step — its real label/why + owning phase.
  const top: NextStep | undefined = rankedSteps[0];
  if (top) {
    const stage = KIND_STAGE[top.kind];
    const pathAction = pathActionForStep(top);
    return {
      key: `step:${top.id}`,
      headline: top.label,
      detail: top.why,
      ...(top.liftsBandTo ? { liftsBandTo: top.liftsBandTo } : {}),
      ctaLabel: pathAction.label,
      targetStage: stage,
      targetPhase: phaseForStage(stage),
      pathAction,
    };
  }

  // 6. Nothing open — preserve the compatibility fallback, else clear.
  const recommendedAnalyses = state.recommendNext ?? state.recommendNextAnalyses ?? [];
  if (recommendedAnalyses.length > 0) {
    const n = recommendedAnalyses.length;
    return {
      key: 'generate',
      headline: `Run ${n} required analys${n === 1 ? 'is' : 'es'}`,
      detail: 'The evidence is in place — run the analyses the GO gate still needs.',
      ctaLabel: 'Open analyses',
      targetStage: 'generate',
      targetPhase: 'build',
      pathAction: {
        kind: 'report-progress',
        label: 'Open analysis plan',
        reportType: recommendedAnalyses[0]?.type ?? 'feasibility',
      },
    };
  }

  // Respect an explicitly open legacy Define phase. Current screening unknowns
  // are answered and remain disclosures; the report/evidence gates above apply.
  const recordedUnknowns = state.phases.find((phase) => phase.id === 'define')?.followUpCount ?? 0;
  if (recordedUnknowns > 0 && state.phases.find(phase => phase.id === 'define')?.status !== 'cleared') {
    return {
      key: 'intent-recorded-unknown',
      headline: `${recordedUnknowns} intent answer${recordedUnknowns === 1 ? ' is' : 's are'} recorded as unknown`,
      detail: 'Nothing left to fill in, but the decision stays held until the follow-up is resolved. The analyses carry each follow-up in their recommendations.',
      ctaLabel: 'Review intent',
      targetStage: 'intent',
      targetPhase: 'define',
      pathAction: { kind: 'intent', label: 'Review intent' },
    };
  }

  return {
    key: 'cleared',
    headline: isGo ? 'This site has reached GO' : 'All open items are cleared',
    detail: isGo
      ? 'Record the decision and work the post-GO roadmap.'
      : `Nothing is blocking progress right now${distanceToGo > 0 ? ` — ${distanceToGo.toFixed(1)} decision-points from GO.` : '.'}`,
    ctaLabel: 'Review decision',
    targetStage: 'decision',
    targetPhase: 'decide',
    pathAction: { kind: 'none', label: 'Review decision' },
    cleared: true,
  };
}
